import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { columns } from '../components/manage_orders/gridData';
import { ButtonGroup, Stack, IconButton, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';

import InputIcon from '@mui/icons-material/Input';
import EditIcon from '@mui/icons-material/Edit';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PrintIcon from '@mui/icons-material/Print';
import AddCardIcon from '@mui/icons-material/AddCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';

import CustomSearch from '../components/custom_components/CustomSearch';
import CustomButton from '../components/custom_components/CustomButton';
import CustomSelect from '../components/custom_components/CustomSelect';

import AddToListSelection from '../components/manage_orders/AddToListSelection';
import CreateOrder from '../components/manage_orders/CreateOrder';
import EditOrder from '../components/manage_orders/EditOrder';
import PaymentOrder from '../components/manage_payments/PaymentOrder';
import OrderInfo from '../components/manage_orders/OrderInfo';
import MenuOrderStatus from '../components/manage_orders/MenuOrderStatus';

import { checkSameCustomer, useLockBodyScroll } from '../components/utils';
import SearchNotFound from '../utils/SearchNotFound';

import { getPrinterSettings } from '../utils/printerStorage';
import { printLabelReceipt } from '../utils/printLabelReceipt';
import useControlKeys from '../utils/useControlKeys';

export default function ManageOrders() {

    const apiUrl = process.env.REACT_APP_API_URL;
    const printers = getPrinterSettings();
    const receiptPrinter = printers?.receiptPrinter;

    const [data_rows, setRows] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set()
    });

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState("");
    const [limit, setLimit] = useState(60);

    const [isOpenListSelection, setIsOpenListSelection] = useState(false);
    const [isOpenEditOrder, setIsOpenEditOrder] = useState(false);
    const [isOpenPaymentOrder, setIsOpenPaymentOrder] = useState(false);
    const [isOpenCreateOrder, setIsOpenCreateOrder] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);

    useLockBodyScroll(isOpenListSelection);
    useLockBodyScroll(isOpenEditOrder);
    useLockBodyScroll(isOpenPaymentOrder);
    useLockBodyScroll(isOpenCreateOrder);
    useLockBodyScroll(openInfo);

    // ============================================================
    // CONFIG
    // ============================================================

    const CACHE_MAX_SIZE = 30;
    // Cache lifetime
    const CACHE_TTL = 60 * 1000; // 1 minute
    // Search debounce
    const SEARCH_DEBOUNCE = 400;

    const ordersCache = useRef(new Map());
    const abortControllerRef = useRef(null);
    const requestIdRef = useRef(0);

    const getCachedOrders = useCallback((cacheKey) => {

        const cached = ordersCache.current.get(cacheKey);
        if (!cached) {
            return null;
        }

        const age = Date.now() - cached.timestamp;

        if (age > CACHE_TTL) {
            ordersCache.current.delete(cacheKey);
            return null;
        }

        ordersCache.current.delete(cacheKey);
        ordersCache.current.set(cacheKey, cached);

        return cached.data;
    }, []);

    const setCachedOrders = useCallback((cacheKey, data) => {
        // Remove old version
        ordersCache.current.delete(cacheKey);
        // Add newest version
        ordersCache.current.set(
            cacheKey,
            {
                data,
                timestamp: Date.now()
            }
        );

        while (ordersCache.current.size > CACHE_MAX_SIZE) {
            const oldestKey =
                ordersCache.current
                    .keys()
                    .next()
                    .value;
            if (oldestKey !== undefined) {
                ordersCache.current.delete(
                    oldestKey
                );
            }
        }

    }, []);

    // DISPAY ORDER'S
    const fetchOrders = useCallback(async (searchValue, forceRefresh = false) => {

        const cacheKey =
            JSON.stringify({
                search: searchValue,
                status,
                limit
            });

        if (!forceRefresh) {
            const cached = getCachedOrders(cacheKey);
            if (cached) {
                setRows(cached.rows);
                return;
            }
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const requestId = ++requestIdRef.current;

        try {

            setLoadingOrders(true);
            clearSelection();

            const response = await fetch(`${apiUrl}manage_orders/get_all_orders.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    search: searchValue,
                    status,
                    limit,
                })
            });

            if (!response.ok) {
                throw new Error(
                    `HTTP Error ${response.status}`
                );
            }

            const result = await response.json();

            if (requestId !== requestIdRef.current) {
                return;
            }

            if (result.status === "success") {

                // Create a quick payment lookup
                const paymentMap = new Map(
                    (result.payments || []).map((payment) => [
                        String(payment.OrderID),
                        Number(payment.PaidAmount) || 0
                    ])
                );

                const newRows = (result.data || []).map((row) => {

                    const price = Number(row.Price) || 0;
                    const itemQty = Number(row.ItemQty) || 0;
                    const sFee = Number(row.SFee) || 0;

                    // Get payment for this order
                    const paidAmount = paymentMap.get(String(row.OrderID)) || 0;
                    const totalAmount = (price * itemQty) + sFee;
                    const balance = totalAmount - paidAmount;

                    return {
                        ...row,
                        PaidAmount: paidAmount,
                        Balance: balance
                    };
                });

                setCachedOrders(cacheKey, {
                    rows: newRows
                });

                setRows(newRows);

            } else {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message || 'Unable to load orders.',
                    icon: 'error'
                });
            }

        } catch (error) {

            if (error.name === 'AbortError') {
                return;
            }

            if (requestId !== requestIdRef.current) {
                return;
            }

            Swal.fire({
                title: 'Server Error!',
                text: error.message || 'Unable to load orders.',
                icon: 'error'
            });

        } finally {
            if (requestId === requestIdRef.current) {
                setLoadingOrders(false);
            }
        }

    }, [apiUrl, status, limit, getCachedOrders, setCachedOrders]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, SEARCH_DEBOUNCE);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchOrders(debouncedSearch, false);
    }, [debouncedSearch, status,]);

    // ========================================================
    // CLEANUP
    // ========================================================

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // ========================================================
    // FORCE REFRESH
    //
    // Call this after creating/updating/deleting an order.
    // ========================================================

    // const refreshOrders = useCallback(() => {
    //     // Remove current cache
    //     // by simply forcing the request.
    //     fetchOrders(debouncedSearch, true);
    // }, [fetchOrders, debouncedSearch]);

    // ========================================================
    // CLEAR ALL CACHE
    //
    // Useful after major changes.
    // ========================================================

    // const clearOrdersCache = useCallback(() => {
    //     ordersCache.current.clear();
    // }, []);



    const selectedRowsData = useMemo(() => {
        return data_rows.filter(row =>
            selectedIds?.includes(row.id)
        );
    }, [data_rows, selectedIds]);

    const selectedRows = useMemo(() => {
        return selectedRowsData.filter(row =>
            selectedIds?.some(id => id === row.id)
        );
    }, [data_rows, selectedIds]);

    // INVOICE FUNCTION
    // const handleInvoice = () => {
    //     // const selectedRowsData = data_rows.filter(row =>
    //     //     selectedIds?.some(id => id === row.id)
    //     // );

    //     const isSameCustomer = checkSameCustomer(selectedRowsData);

    //     if (!isSameCustomer) {
    //         Swal.fire({
    //             text: "Please select order/s from the same customer!",
    //             icon: "info"
    //         });
    //         return;
    //     }
    //     console.log("Selected Rows:", selectedRowsData);
    // }

    // ADD TO LIST FUNCTION
    const handleAddToList = () => {
        // const selectedRowsData = data_rows.filter(row =>
        //     selectedIds?.some(id => id === row.id)
        // );

        setIsOpenListSelection(true);

        // console.log("Selected Rows:", selectedRowsData);
    }

    // EDIT FUNCTION
    const handleEditOrder = () => {
        if (selectedRowsData.length > 1) {
            Swal.fire({ text: "Please select a single order to edit!", icon: 'info' });
            return;
        }
        setIsOpenEditOrder(true);
    }

    // OPEN PAYMENT FUNCTION
    const handlePaymentOrder = () => {
        if (selectedRowsData.length > 1) {
            Swal.fire({ text: "Please select a single order for payment!", icon: 'info' });
            return;
        }

        setIsOpenPaymentOrder(true);
    }

    // PRINT LABEL FUNCTION
    const handlePrintLabel = async () => {
        if (!checkSameCustomer(selectedRowsData)) {
            Swal.fire({
                text: "Please select order/s from the same customer!",
                icon: "info"
            });
            return;
        }

        // 🖨️ CHECK PRINTER
        if (!receiptPrinter?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Printer Required',
                text: 'Please configure a Reciept Printer in Printer Settings.'
            });
            return;
        }

        try {

            await printLabelReceipt(receiptPrinter, selectedRowsData);

        } catch (err) {
            console.error(err);

            Swal.fire({
                icon: "error",
                text: "Printing failed."
            });
        }
    };

    const computeOrderAmount = (item) => {
        return (
            (Number(item?.Price || 0) *
                Number(item?.ItemQty || 0)) +
            Number(item?.SFee || 0)
        );
    };

    const clearSelection = () => {
        setRowSelectionModel({
            type: 'include',
            ids: new Set()
        });
        setSelectedIds([]);
    };

    useControlKeys({
        onPrint: handlePrintLabel,
    });

    return (
        <Stack direction="column" gap={2}>

            {/* ACTION CONTROLS */}
            <Stack direction='row' justifyContent='space-between'>
                <Stack direction='row' gap={2}>
                    <CustomSearch
                        md='36ch'
                        placeholder='Search by Customer Name'
                        value={search}
                        onChange={(val) => setSearch(val)}
                    />

                    <IconButton
                        aria-label="refresh"
                        size="small"
                        disabled={loadingOrders}
                        onClick={() => fetchOrders(debouncedSearch, true)}
                        sx={{
                            width: 36,
                            height: 36,

                            backgroundColor: "#1976d2",
                            color: "#ffffff",

                            borderRadius: "8px",

                            transition: "all .2s ease",

                            "&:hover": {
                                backgroundColor: "#3B82F6",
                                borderColor: "#3B82F6",
                            },

                            '&:disabled': {
                                backgroundColor: '#e0e0e0', // soft gray
                                color: '#9e9e9e',           // muted text
                                boxShadow: 'none',
                            }
                        }}
                    >
                        {loadingOrders ? (
                            <CircularProgress
                                size={18}
                                thickness={5}
                                color="inherit"
                            />
                        ) : (
                            <RefreshIcon fontSize="small" />
                        )}
                    </IconButton>

                    <CustomSelect
                        label="Filter by Status"
                        disabled={loadingOrders}
                        minWidth={160}
                        value={status}
                        onChange={setStatus}
                        options={[
                            { label: <em>All</em>, value: "" },
                            { label: "Pending", value: "Pending" },
                            { label: "Confirmed", value: "Confirmed" },
                            { label: "In Transit", value: "In_Transit" },
                            { label: "Arrived", value: "Arrived" },
                            { label: "Shipped", value: "Shipped" },
                            { label: "Cancelled", value: "Cancelled" },
                            { label: "Forfeited", value: "Forfeited" },
                            { label: "Disposed", value: "Disposed" },
                        ]}
                    />

                </Stack>

                {/* ACTION BUTTONS  */}
                <ButtonGroup direction='row' gap={2}>
                    <CustomButton
                        label="Create"
                        disabled={loadingOrders}
                        startIcon={<AddShoppingCartIcon />}
                        onClick={() => setIsOpenCreateOrder(true)}
                    />

                    <CustomButton
                        label="Add to List"
                        disabled={selectedIds.length === 0}
                        startIcon={<InputIcon />}
                        onClick={handleAddToList}
                    />

                    <CustomButton
                        label="Edit"
                        disabled={selectedIds.length === 0 || selectedIds.length > 1}
                        startIcon={<EditIcon />}
                        onClick={() => handleEditOrder()}
                    />

                    <CustomButton
                        label="Payment"
                        disabled={selectedIds.length === 0 || selectedIds.length > 1}
                        startIcon={<AddCardIcon />}
                        onClick={() => handlePaymentOrder()}
                    />

                    <CustomButton
                        label="Print"
                        disabled={selectedIds.length === 0}
                        startIcon={<PrintIcon />}
                        onClick={handlePrintLabel}
                    />

                    <CustomButton
                        label="Info"
                        disabled={selectedIds.length === 0 || selectedIds.length > 1}
                        startIcon={<InfoIcon />}
                        onClick={() => setOpenInfo(true)}
                    />

                    <MenuOrderStatus
                        orders={selectedRowsData || []}
                        disabled={selectedIds.length === 0}

                        onUpdate={(val) =>
                            setRows((prev) => {

                                const updatedOrders = new Set(
                                    (val.orders || []).map(
                                        (order) =>
                                            `${order.CustomerID}-${order.OrderID}`
                                    )
                                );

                                return prev.map((item) => {

                                    const key =
                                        `${item.CustomerID}-${item.OrderID}`;

                                    return updatedOrders.has(key)
                                        ? {
                                            ...item,
                                            OrderStat: val.status,
                                        }
                                        : item;
                                });
                            })
                        }
                    />

                </ButtonGroup>
            </Stack>

            {/* DATAGRID */}
            <DataGrid
                autoHeight
                checkboxSelection
                rows={data_rows}
                columns={columns}
                loading={loadingOrders}
                localeText={{
                    noRowsLabel: loadingOrders ? "" : (<SearchNotFound />)
                }}

                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newSelection) => {
                    let ids = [];
                    if (newSelection.type === 'include') {
                        // Normal selection
                        ids = Array.from(newSelection.ids);
                    } else if (newSelection.type === 'exclude') {
                        // SELECT ALL → get all IDs except excluded
                        ids = data_rows
                            .filter(row => !newSelection.ids.has(row.id))
                            .map(row => row.id);
                    }
                    setSelectedIds(ids);
                    setRowSelectionModel(newSelection);
                }}

                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
                initialState={{
                    pagination: { paginationModel: { pageSize: 20 } },
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                disableColumnResize
                disableColumnMenu
                disableColumnSorting
                disableRowSelectionOnClick
                density="compact"
                sx={{
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#1C2536',
                        borderBottom: 'none',
                    },

                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#1C2536',
                    },

                    '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#94A3B8',
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                    },

                    // ✅ REMOVE CLICK BORDER
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none',
                    },
                    // 🟢 NORMAL HOVER
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#dcfbff',
                        cursor: 'pointer',
                    },

                    // 🔵 SELECTED ROW
                    '& .MuiDataGrid-row.Mui-selected': {
                        backgroundColor: '#cfe8ff',
                    },

                    // 🔵 SELECTED + HOVER
                    '& .MuiDataGrid-row.Mui-selected:hover': {
                        backgroundColor: '#b6dcff',
                    },
                }}

                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                    },
                    filterPanel: {
                        filterFormProps: {
                            logicOperatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                            },
                            columnInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            operatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                                sx: { mt: 'auto' },
                            },
                            valueInputProps: {
                                InputComponentProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                },
                            },
                        },
                    },
                }}
            />

            {/* LIMIT DISPLAY */}
            <Stack direction="row">
                <CustomSelect
                    label="Display Limit"
                    minWidth={160}
                    disabled={loadingOrders}
                    value={limit}
                    onChange={setLimit}
                    options={[
                        { label: <em>Default 60</em>, value: 60 },
                        { label: "100", value: 100 },
                        { label: "200", value: 200 },
                        { label: "300", value: 300 },
                        { label: "500", value: 500 },
                    ]}
                />
            </Stack>

            {/* CREATE ORDER */}
            <CreateOrder
                open={isOpenCreateOrder}
                onClose={() => setIsOpenCreateOrder(false)}
                onUpdate={(vals) => {
                    setRows(prev => [
                        ...(Array.isArray(vals) ? vals : [vals]),
                        ...prev
                    ]);
                }}
            />

            {/* ADD TO LIST ORDER */}
            <AddToListSelection
                open={isOpenListSelection}
                onClose={() => setIsOpenListSelection(false)}
                selectedRows={selectedRows}
                onUpdate={(val) =>
                    setRows(prev =>
                        prev.map(item =>
                            val.some(
                                order =>
                                    order.OrderID === item.OrderID &&
                                    order.CustomerID === item.CustomerID
                            )
                                ? {
                                    ...item,
                                    OrderStat: "Confirmed",
                                }
                                : item
                        )
                    )
                }
            />

            {/* EDIT ORDER */}
            <EditOrder
                open={isOpenEditOrder}
                onClose={() => setIsOpenEditOrder(false)}

                orderInfo={selectedRowsData[0] || {}}
                onUpdate={(val) =>
                    setRows(prev =>
                        prev.map(item =>
                            item.OrderID === val.orderID &&
                                item.CustomerID === val.customerID
                                ? {
                                    ...item,
                                    ItemName: val.ItemName,
                                    ItemQty: val.ItemQty,
                                    ItemCategory: val.ItemCategory,
                                    Price: val.Price,
                                    SFee: val.SFee,
                                    Cargo: val.Cargo,
                                    Balance:
                                        computeOrderAmount(val) -
                                        Number(item.PaidAmount || 0),
                                }
                                : item
                        )
                    )
                }
            />

            {/* ADD PAYMENT FOR ORDERS */}
            <PaymentOrder
                open={isOpenPaymentOrder}
                onClose={() => setIsOpenPaymentOrder(false)}

                orderInfo={selectedRowsData[0] || {}}
                onUpdate={(val) => setRows(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PaidAmount: val.PaidAmount,
                                Balance:
                                    computeOrderAmount(item) -
                                    Number(val.PaidAmount || 0),
                            }
                            : item
                    )
                )}
                onUpdateStatus={(val) => setRows(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PayStatus: val.payStatus,
                            }
                            : item
                    )
                )}
            />

            {/* ORDER INFORMATIONS */}
            <OrderInfo
                open={openInfo}
                order={selectedRowsData[0] || {}}
                onClose={() => setOpenInfo(false)}
            />

        </Stack>
    );
}
